// The gesture island entry point.
//
// The island is the only client code: a framework-free canvas + overlay layer
// that handles pointer drag / resize / stack and runs stroke recognition, then
// dispatches recognised strokes to the server as hx-posts (canon 91 §1). The
// recognizer ports from the frontend; it lands in K-91f. The overlay drag /
// resize / stack of the field's challenge panes lands in lane L-OVL (overlay.ts
// + overlay-geometry.ts): the opened challenge is a projection over the feed
// (canon 75), and a person can move it, resize it, and stack panes, in the
// electric register (canon 94) with no cursor ever drawn (canon 83 §1.4).
//
// K-94b adds the ELECTRIC interaction (canon 94 §2): the field is a circuit that
// carries what people did. The render layer emits the field's STRUCTURE and marks
// what is live; the island paints the LIVENESS over it — lit wires carrying
// current, nodes that energize their connections, rings that race their rim, far
// objects that arrive lit. The effect module is `electric.ts`; this entry wires
// it onto the mounted field and re-paints whenever the field fragment is swapped
// (an HTMX `hx-post` returns new structure) or the SSE stream signals a liveness
// change.
//
// K-91f adds the STROKE RECOGNIZER (canon 77, canon 75 §3): the client half of
// the grammar. The server (internal/stroke) has interpreted the six strokes
// since K-91e, but nothing turned a person's pointer drag into a recognised
// stroke — so no act could be DRAWN. `gestures.ts` captures pointer gestures
// over the mounted field, draws the person's ink (no cursor — canon 83 §1.4),
// classifies the gesture into one of the six strokes (recognizer.ts, pure), and
// posts the recognised stroke to the server's PostStroke endpoint. This entry
// attaches it alongside the electric liveness painting, and re-attaches it after
// an HTMX swap replaces the field fragment.

import { driveField, type LiveHandle } from "./electric.js";
import { paintWordLines, type WordLinesHandle } from "./wordlines.js";
import { attachRecognizer, type RecognizerHandle } from "./gestures.js";
import { mountOverlays, type OverlayHandle } from "./overlay.js";
import { attachSwapTransitions, type SwapHandle } from "./swap.js";
import { initFaro } from "./faro.js";
import { connectSignal } from "./signal.js";

// Browser RUM (Grafana Faro) is armed first, before the field paints, so a
// first-paint error or the page-load timing is captured. It is a NO-OP unless
// the server rendered a collector URL into the page (OBJEXIV_FARO_COLLECTOR_URL
// → the `objexiv:faro-url` meta tag); dev and test have no collector set, so
// nothing is loaded or sent there. Framework-free (canon 91 §1): Faro is a small
// instrumentation lib wired at the entry, not an SPA framework.
initFaro();

/**
 * mount wires the island onto the field element. It paints the electric circuit
 * over whatever field SVG is present, and keeps a handle so a later re-paint can
 * clear the previous liveness first (no leaked animations across swaps).
 */
export function mount(root: HTMLElement): void {
  root.dataset.island = "ready";

  let current: LiveHandle | null = null;

  const paint = () => {
    current?.clear();
    current = null;
    const svg = root.querySelector<SVGSVGElement>("svg");
    if (svg) current = driveField(svg);
  };

  paint();

  // The WORD-LINES painter (K-D3, canon 86 §3): word↔word and word↔user
  // connection lines between the field's key-word objects. It builds its own SVG
  // overlay inside the field root and paints the shared-language / word-to-author
  // lines in the electric register (electric.ts). It is attached ONCE and
  // re-measured on each HTMX swap below (new words/takes appear) and on its own
  // resize listener; the overlay sits behind the words so the lit words read as
  // ON their connections. Harmless where no key words are present (the feed /
  // firsts-demo carry none) — it simply draws nothing.
  const wordLines: WordLinesHandle = paintWordLines(root);

  // The stroke recognizer is attached to the field root ONCE and stays attached
  // across swaps: it reads the live DOM on each gesture (hit-testing the objects
  // present and the pane→challenge map at that moment), so a fragment swap does
  // not stale it. Re-painting liveness on swap stays separate — that clears and
  // redraws per-element handles the new structure needs.
  const recognizer: RecognizerHandle = attachRecognizer(root);
  void recognizer; // attached for the lifetime of the mounted field

  // The OVERLAY interaction (lane L-OVL): makes the field's challenge panes
  // movable / resizable / stackable (canon 75 — the opened challenge is a
  // projection over the feed; split is two live panes). Attached to the same
  // root ONCE; it owns only the pane FRAME (a grip to move, the edges to resize)
  // and leaves the pane BODY to the recognizer, so the two pointer interactions
  // do not fight (see overlay.ts for the reconciliation rule). After an HTMX
  // swap replaces the field fragment, the overlay re-scans for the new panes.
  const overlays: OverlayHandle = mountOverlays(root);

  // The SWAP TRANSITION (ISLAND PR2 — "kill the blink"): graceful enter/exit +
  // FLIP across every HTMX fragment swap under the field, so a reordered feed's
  // items MOVE to their new places instead of blinking there (canon 75 §1 rule
  // 2), an opened challenge / appearing pane ENTERS, a leaving fragment EXITS,
  // and a tie's proposed→accepted weight SETTLES (canon 77 §3). It listens for
  // htmx:beforeSwap (capture) and htmx:afterSwap (play) on this same root, all
  // motion through motion.ts (the one motion language), reduced-motion honoured
  // there. Attached ONCE and left for the field's lifetime; harmless where htmx
  // is absent (the demo harness drives capture()/play() directly).
  const swaps: SwapHandle = attachSwapTransitions(root);
  void swaps;

  // Re-paint after an HTMX swap replaces the field fragment with new structure.
  // htmx dispatches `htmx:afterSwap` on the swapped target; we listen on the root
  // so a swap of any descendant re-paints. The listener is harmless where HTMX is
  // absent (the demo harness drives paint() / overlays.refresh() directly).
  root.addEventListener("htmx:afterSwap", (() => {
    paint();
    overlays.refresh();
    // New structure can carry new key words / takes, and the reflow moves the
    // existing ones — re-measure and re-draw the word lines so a word that just
    // arrived joins the shared-word / word-to-author lines live (canon 91 §2a).
    wordLines.repaint();
  }) as EventListener);

  // The LIVING SIGNAL (canon 91 §2a): open the realtime change-ping stream and
  // route each ping to the feed as an `sse:message` event (the feed's
  // hx-trigger re-pulls the list; htmx owns the swap). The island owns this
  // connection — NOT the htmx SSE extension — precisely so a down or absent
  // /signal (a worker/hub blip in prod) degrades SILENTLY with no console error:
  // the field is fully usable without it, and the browser auto-reconnects a
  // transient drop. See signal.ts for why the extension could not provide this.
  connectSignal();
}

const field = document.getElementById("field");
if (field) {
  mount(field);
}
